import { Box, Divider, Stack, useMediaQuery, useTheme } from '@mui/material'
import { axisClasses } from '@mui/x-charts'
import { BarChart } from '@mui/x-charts/BarChart'
import Holidays from 'date-holidays'
import dayjs from 'dayjs'
import sum from 'lodash/sum'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import LoadingWrapper from '../../components/Loading/LoadingWrapper'
import { VacationsTable } from '../../components/Table/VacationsTable'
import MonthTile from '../../components/Tile/MonthTile'
import { YearlySectionHeader } from '../../components/ToggleDayHoursRight/YearlySectionHeader'
import UserSelector from '../../components/UserSelector/UserSelector'
import navigation from '../../config/navigation'
import { useAppDispatch, useAppSelector } from '../../store/redux/hooks'
import { breadcrumbsItemsActions } from '../../store/redux/reducers/breadcrumbsSlice'
import { getCurrentUser, getIsLeaderOrManagement } from '../../store/redux/reducers/currentUserSlice'
import { getSelectedDate, monthPickerActions } from '../../store/redux/reducers/monthPickerSlice'
import { pageHeaderActions } from '../../store/redux/reducers/pageHeaderSlice'
import { PageIndex, setPageIndex, setPageName } from '../../store/redux/reducers/pageNameSlice'
import { getSidebarState } from '../../store/redux/reducers/sidebarSlice'
import { useGetLightUsersQuery } from '../../store/redux/reducers/usersSlice'
import { useGetYearlySummaryQuery } from '../../store/redux/reducers/yearlySummarySlice'
import { User } from '../../store/types'
import checkAccessDenied from '../../store/utils/checkAccessDenied'
import { getDataForGraph, SummaryData } from './YearlySummaryCalculationFunctions'
import WorkingHoursPercentageBar from '../../components/PercentageBar/WorkingHoursPercentageBar'
import VacationPercentageBar from '../../components/PercentageBar/VacationPercentageBar'

const YearlySummaryView = () => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const selectedDate = useAppSelector(getSelectedDate)
  const currentUser = useSelector(getCurrentUser)
  const isLeaderOrManagement = useSelector(getIsLeaderOrManagement)

  const [daysMode, setDaysMode] = useState<boolean[]>([false, false])

  const theme = useTheme()
  const isDownLg = useMediaQuery(theme.breakpoints.down('lg'))
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))

  const sidebarOpen = useAppSelector(getSidebarState)
  const effectiveSmallScreenWidth = `(max-width:${800 + (sidebarOpen ? 250 : 75)}px)`
  const isDownLgWithSidebar = useMediaQuery(effectiveSmallScreenWidth)

  const effectiveMediumScreenWidth = `(max-width:1150px) and (min-width:${800 + (sidebarOpen ? 250 : 75)}px)`
  const isMediumScreenWithSidebar = useMediaQuery(effectiveMediumScreenWidth)

  const { paramId } = useParams()
  const id = paramId ?? currentUser?.id

  useEffect(() => {
    if (!selectedDate) {
      dispatch(monthPickerActions.setDate(dayjs().toString()))
    }
  }, [dispatch, selectedDate])

  const { data: selectedUserData } = useGetLightUsersQuery(
    {
      page: 0,
      size: 1,
      id: id || currentUser?.id,
    },
    { skip: !isLeaderOrManagement }
  )

  const [selectedUser, setSelectedUser] = useState<User | null>(currentUser)

  useEffect(() => {
    if (selectedUserData) {
      setSelectedUser(selectedUserData.content[0])
    }
  }, [selectedUserData])

  const [idForRequest, setIdForRequest] = useState<string>('')

  useEffect(() => {
    setIdForRequest(selectedUser?.id ?? '')
  }, [selectedUser])

  const {
    data: yearlySummary,
    isLoading,
    isUninitialized,
    isError,
    error,
  } = useGetYearlySummaryQuery(
    {
      id: idForRequest,
      year: dayjs(selectedDate).year(),
    },
    { skip: idForRequest === '' }
  )

  useEffect(() => {
    dispatch(setPageName(t('page.name.yearlySummary')))
    dispatch(setPageIndex(PageIndex.YEARLY_SUMMARY))
    dispatch(pageHeaderActions.setButtons({ yearPicker: true }))
    dispatch(monthPickerActions.setDate(dayjs().toString()))
    return () => {
      dispatch(pageHeaderActions.setInitialState())
    }
  }, [dispatch, t])

  useEffect(() => {
    if (selectedUser) {
      dispatch(breadcrumbsItemsActions.setInitialState())
      dispatch(
        breadcrumbsItemsActions.setSelectedItems({
          yearlySummary: `${t('page.name.yearlySummary')} - ${selectedUser.firstName} ${selectedUser.lastName}`,
        })
      )
    }
  }, [dispatch, selectedUser, t])

  useEffect(() => {
    if (error) {
      if (checkAccessDenied(error)) {
        navigate(navigation.unauthorized)
      }
    }
  }, [error, isError, navigate])

  const workDaysInYear = useMemo(() => {
    if (!selectedDate) return []

    const year = dayjs(selectedDate).year()
    const holidays = new Holidays('PL', '', '', { types: ['public'] })

    const statutoryHolidays = Object.values(holidays.getHolidays(year))
      .filter(holiday => holiday.type === 'public')
      .map(holiday => dayjs(holiday.date))

    return Array.from({ length: 12 }, (_, month) => {
      const daysInMonth = dayjs().year(year).month(month).daysInMonth()
      return Array.from({ length: daysInMonth }, (_, day) =>
        dayjs()
          .year(year)
          .month(month)
          .date(day + 1)
      )
        .filter(date => {
          const isWeekend = date.day() === 0 || date.day() === 6
          const isHoliday = statutoryHolidays.some(holiday => date.isSame(holiday, 'day'))
          return !isWeekend && !isHoliday
        })
        .reduce(acc => acc + 8, 0)
    })
  }, [selectedDate])

  const {
    workedHoursGraphData,
    leftworkHoursGraphData,
    overtimeHoursGraphData,
    paidVacationHoursGraphData,
    unpaidVacationHoursGraphData,
    usedOvertimeGraphData,
    sumWorked,
    sumOvertime,
    usedOvertime,
    sumVacation,
    sumPaidVacation,
    sumUnpaidVacation,
    sumLeft,
  }: SummaryData = useMemo<SummaryData>(() => {
    return getDataForGraph(yearlySummary, workDaysInYear)
  }, [yearlySummary, workDaysInYear])

  const xLabels = Array.from({ length: 12 }, (_, month) => dayjs().month(month).format('MMM'))
  const monthLabels = Array.from({ length: 12 }, (_, month) => dayjs().month(month).format('MMMM'))
  const valueFormatter = (value: number) =>
    daysMode[0] ? Math.round((value * 10) / 8) / 10 + 'd' : value.toFixed(2) + 'h'

  return (
    <LoadingWrapper isLoading={isLoading || isUninitialized}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, px: 2, width: '100%', height: '100%' }}>
        <Stack direction="row" justifyContent="space-between" py={2} sx={{ paddingBottom: 0, height: '5%' }}>
          <Stack sx={{ width: { xs: '100%', md: '50%', lg: '25%' }, marginBottom: 0 }}>
            {isLeaderOrManagement && (
              <UserSelector
                selectedUser={selectedUser}
                setSelectedUser={(user: User | null) => {
                  setSelectedUser(user)
                }}
                currentUser={currentUser}
                currentView={navigation.yearlySummary}
              />
            )}
          </Stack>
        </Stack>
        <Box
          sx={{
            display: 'flex',
            flexDirection: isDownLg ? 'column' : 'row',
            gap: 4,
            px: isDownSm ? 0 : 2,
            width: '100%',
            paddingTop: 2,
            height: isDownLg ? 'auto' : '100%',
            maxHeight: isDownLg ? '1400px' : '900px',
            minHeight: isDownLg ? '900px' : '300px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: isDownLg ? '100%' : '50%',
              height: '100%',
              maxHeight: isDownLg ? '600px' : '900px',
              minHeight: '300px',
            }}
          >
            <YearlySectionHeader
              title={t('page.yearlySummary.title.graph')}
              variant={'h2'}
              daysMode={daysMode}
              setDaysMode={setDaysMode}
              index={0}
            />
            <BarChart
              series={[
                {
                  data: workedHoursGraphData,
                  label: t('page.yearlySummary.barGraph.worked'),
                  id: 'pvId',
                  stack: 'total',
                  color: theme.palette.primary.light,
                  valueFormatter,
                },
                {
                  data: paidVacationHoursGraphData,
                  label: t('page.yearlySummary.barGraph.paidVacation'),
                  id: 'ppvId',
                  stack: 'total',
                  color: theme.palette.info.dark,
                  valueFormatter,
                },
                {
                  data: unpaidVacationHoursGraphData,
                  label: t('page.yearlySummary.barGraph.unpaidVacation'),
                  id: 'uuvId',
                  stack: 'total',
                  color: theme.palette.info.light,
                  valueFormatter,
                },
                {
                  data: overtimeHoursGraphData,
                  label: t('page.yearlySummary.barGraph.overtime'),
                  id: 'ovId',
                  stack: 'total',
                  color: theme.palette.error.light,
                  valueFormatter,
                },
                {
                  data: usedOvertimeGraphData,
                  label: t('page.yearlySummary.barGraph.usedOvertime'),
                  id: 'ovUsedId',
                  stack: 'total',
                  color: theme.palette.success.light,
                  valueFormatter,
                },
                {
                  data: leftworkHoursGraphData,
                  label: t('page.yearlySummary.barGraph.left'),
                  id: 'uvId',
                  stack: 'total',
                  color: theme.palette.secondary.light,
                  valueFormatter,
                },
              ]}
              xAxis={[{ data: xLabels, scaleType: 'band' }]}
              yAxis={[
                {
                  scaleType: 'linear',
                  valueFormatter,
                  label: daysMode[0] ? t('page.yearlySummary.barGraph.days') : t('page.yearlySummary.barGraph.hours'),
                  max: 200,
                },
              ]}
              slotProps={{ legend: { hidden: true } }}
              margin={{ left: 80, right: 0, top: 20, bottom: 20 }}
              sx={{
                [`.${axisClasses.left} .${axisClasses.label}`]: {
                  transform: 'translate(-35px, 0)',
                  textAnchor: 'middle',
                },
                [`.${axisClasses.right} .${axisClasses.label}`]: {
                  transform: 'translate(30px, 0)',
                },
              }}
            />
            <WorkingHoursPercentageBar
              sumWorked={sumWorked}
              sumOvertime={sumOvertime}
              usedOvertime={usedOvertime}
              sumVacation={sumVacation}
              sumPaidVacation={sumPaidVacation}
              sumUnpaidVacation={sumUnpaidVacation}
              sumAll={sum(workDaysInYear)}
              isDisplayDaysMode={daysMode[0]}
              sumLeft={sumLeft}
            />
          </Box>
          {!isDownLg ? <Divider orientation="vertical" variant="fullWidth" /> : <Divider />}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: isDownLg ? '100%' : '50%',
              height: '100%',
              maxHeight: isDownLg ? '600px' : '900px',
              minHeight: '300px',
            }}
          >
            <YearlySectionHeader
              title={t('page.yearlySummary.title.vacation')}
              variant={'h2'}
              daysMode={daysMode}
              setDaysMode={setDaysMode}
              index={1}
            />
            <VacationsTable yearlySummary={yearlySummary} daysMode={daysMode[1]} />
            <VacationPercentageBar
              usedPaidVacation={sumPaidVacation}
              paidVacationHoursTotal={yearlySummary?.paidVacationHours || 0}
              allUnpaidVacationSum={sumUnpaidVacation}
              isDisplayDaysMode={daysMode[1]}
            />
          </Box>
        </Box>
        <Divider />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            px: isDownSm ? 0 : 2,
            width: '100%',
            height: 'auto',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: isDownLgWithSidebar
                ? 'repeat(1, 1fr)'
                : isMediumScreenWithSidebar
                ? 'repeat(2, 1fr)'
                : 'repeat(auto-fill, minmax(24%, 1fr))',
              gap: '16px',
              paddingBottom: 3,
            }}
          >
            {monthLabels.map((month, index) => (
              <MonthTile
                key={index}
                title={month}
                workedHours={workedHoursGraphData[index] + overtimeHoursGraphData[index]}
                vacationHours={paidVacationHoursGraphData[index] + unpaidVacationHoursGraphData[index]}
                index={index}
                year={dayjs(selectedDate).year()}
                selectedUser={selectedUser}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </LoadingWrapper>
  )
}

export default YearlySummaryView
